import videojs from 'video.js';
import 'videojs-contrib-hls';
import isSafari from '../utils/isSafari';
import EventBus from '@/utils/EventBus';

export const bufferCache = {};

export function cachedBuffer(clip, isPano) {
    const key = makeBufferKey(clip, isPano);

    return bufferCache[key] || (bufferCache[key] = new CachedBuffer(clip));
}

export function makeBufferKey(clip, isPano) {
    return `${isPano ? 'pano_' : ''}${clip.video.id}_${clip.start_time}_${clip.end_time}`;
}

const playerCache = {};

let nextId = 0;

export class CachedBuffer {
    constructor(clip) {
        this.id = nextId++;
        this.events = new EventBus();

        this.clip = clip;
        this.startSeconds = (clip.start_time || 0) / 1000;
        this.endSeconds = (clip.end_time || clip.video.durationInMilliseconds) / 1000;

        // Is the video currently scrubbed to a time that's within bounds for the clip?
        this.isInBounds = false;

        // Is the video buffered enough to play?
        this.canPlay = true;

        // Should the video be playing if able to?
        this.playRequested = false;

        // The last time that we programmatically attempted to scrub to
        this.lastScrubbedTime = this.startSeconds;

        // Seconds to skip at the beginning for Chrome workaround
        this.skipSeconds = 0.1;

        // Is this the currently focused clip?
        this.isActive = false;

        if (clip.video.deleted) {
            // Stop buffering deleted video
            return
        }

        this.player = this.acquirePlayer();
        this.player.ready(() => this.onReady());

        this._checkTime();
        this.pause();
        this.hide();
    }

    activate() {
        if (this.isMp4Video()) {
            this.player = this.createPlayer();
            this.player.ready(() => this.onReady());
        }

        this.isActive = true;
        this.show();
        this._checkTime();
    }

    deactivate() {
        this.pause();
        this.hide();

        if (this.isMp4Video()) {
            this.player.dispose();
        }

        this.isActive = false;
    }

    show() {
        $(this.player.contentEl()).show();
    }

    hide() {
        $(this.player.contentEl()).hide();
    }

    getVideoElement() {
        return this.player.contentEl();
    }

    acquirePlayer() {
        const cacheKey = this.clip.video.id;
        return playerCache[cacheKey] || (playerCache[cacheKey] = this.createPlayer());
    }

    removePlayerFromCache() {
        const cacheKey = this.clip.video.id;
        delete playerCache[cacheKey];
    }

    createPlayer() {
        const sourceElement = this.createSourceElement();
        this.createContainerElement().appendChild(sourceElement);
        this.updateEditorStyles();

        const player = videojs(sourceElement, {
            controls: false,
            muted: isSafari(), // Set muted by default in Safari to allow autoplay
            preload: 'auto',
        });

        player.volume(0.5);
        player.loadingSpinner.hide();

        return player;
    }

    createContainerElement() {
        return $('#buffer-mount').get(0);
    }

    createSourceElement() {
        const sourceUrl = this.getUrl(this.clip.video);
        const type = this.getType(this.clip.video);

        return $(`
                <video class="video-js vjs-default-skin" playsinline>
                    <source src="${sourceUrl}" type="${type}">
                </video>
            `).get(0);
    }

    getUrl(video) {
        const { videoUrl, hostId, host } = video;

        if (host === "connectsports") {
            return video.playableHostId;
        }

        return videoUrl ? videoUrl : `https://video-content.sportsrecruits.com/${hostId}/hls/index.m3u8`;
    }

    getType(video) {
        if(video.host == 'connectsports') {
            return "video/mp4";
        }

        return video.hasOwnProperty("videoType") ? video.videoType || '' : "application/x-mpegURL";
    }

    isMp4Video() {
        return this.getType(this.clip.video) === "video/mp4";
    }

    updateEditorStyles() {
        $("#buffer-mount")
            .css("background-color", "black")
            .css("border-radius", "5px");
    }

    onReady() {
        this._checkTime()
        this.player.on('timeupdate', () => this._checkTime())
        this.player.on('canplay', () => this.onCanPlay())
        this.player.on('waiting', () => this.onWaiting())
        this.player.on('ended', () => this.onEnded());
    }

    onCanPlay() {
        this.canPlay = true

        if (this.canPlay && this.isActive && this.playRequested) {
            this.player.play()
        }
    }

    onEnded() {
        this._setCurrentTime(this.endSeconds);

        this.pause();

        this.events.fire('finish');
    }

    onWaiting() {
        this.canPlay = false
    }

    _setCurrentTime(time) {
        // Prevent weird Chrome stuttering bug
        this.player.currentTime(Math.max(this.skipSeconds, time))
    }

    getCurrentTime() {
        const time = this.player.currentTime()
        return time <= this.skipSeconds && this.lastScrubbedTime <= this.skipSeconds
            ? this.lastScrubbedTime
            : time
    }

    _checkTime() {
        const currentTime = this.player.currentTime()

        if (this.isActive) {
            // We fuzz this a little bit (+/- 1 second) because of imprecision in some browsers.
            if (currentTime < this.lastScrubbedTime - 1 || currentTime > this.endSeconds + 1) {
                this.isInBounds = false

                // Try to get in bounds
                this._setCurrentTime(this.lastScrubbedTime)
            } else {
                this.isInBounds = true
            }

            this.events.fire('timeUpdate', this.getCurrentTime())

            if (currentTime >= this.endSeconds) {
                if (currentTime > this.endSeconds + 0.01) {
                    this._setCurrentTime(this.endSeconds)
                }

                this.events.fire('finish');
            }
        }
    }

    pause() {
        this.playRequested = false

        if (this.isActive) {
            this.player.pause()
        }
    }

    resume() {
        this.playRequested = true

        setTimeout(() => {
            if (this.canPlay && this.isActive && this.playRequested) {
                this.player.play()
            }
        })
    }

    setPlaybackRate(rate) {
        this.player.playbackRate(rate);
    }

    isAtEnd() {
        return this.getCurrentTime() >= this.endSeconds - 0.01 // fuzz for floating point precision
    }

    scrubToRelativeFraction(fraction) {
        this.scrubToRelativeTime(fraction * (this.endSeconds - this.startSeconds))
    }

    scrubToRelativeTime(relativeTime) {
        this.scrubToAbsoluteTime(this.startSeconds + relativeTime)
    }

    scrubToAbsoluteTime(time) {
        time = Math.max(this.startSeconds, Math.min(this.endSeconds, time))

        this.lastScrubbedTime = time
        this._setCurrentTime(time)

        this.events.fire('timeUpdate', time)
    }

    skipNSeconds(seconds) {
        const calculatedTime = this.getCurrentTime() + seconds
        this._setCurrentTime(calculatedTime)
    }

    on(...args) {
        return this.events.on(...args)
    }

    off(...args) {
        return this.events.off(...args)
    }
}